import React, { lazy } from 'react';
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes
} from 'react-router-dom';

import AppShell from './AppShell';
import BrandGuard from './BrandGuard';
import DefaultRedirect from './DefaultRedirect';
import FeatureGuard from './FeatureGuard';
import { LegacyAppRedirect, LegacyAuthRedirect } from './LegacyRedirect';
import ProtectedRoute from './ProtectedRoute';
import ScrollToTop from './ScrollToTop';

// Static, unlike every other page in this file. Both public pages are
// prerendered to static HTML (scripts/prerender.mjs); a lazy chunk would make
// the client's first paint the Suspense fallback, blanking that markup before
// the real page renders. The landing page is also the entry point, so it belongs
// in the initial bundle regardless.
import ContactUs from '@/pages/public/pages/ContactUs';
import RaaSLanding from '@/pages/public/pages/RaaSLanding';
import HubSpotPageViews from '@/shared/analytics/HubSpotPageViews';
import { NEWSROOM_V3_ENABLED } from '@/shared/featureFlags';
import {
  PROGRAM_EDITORS,
  PROGRAM_STEP_ROUTES,
  PROGRAM_STEPS_FALLBACK_PATH,
  type ProgramEditorKey,
  programHomePath,
  type ProgramStepRoute,
  ROADMAP_STEP_PARAM,
  stepRequiredFeatures
} from '@/shared/programs/registry';
import {
  APP_ROUTES,
  APP_SEGMENTS,
  LEGACY_APP_PATHS,
  LEGAL_ROUTES,
  LOGIN_ROUTES,
  PUBLIC_ROUTES,
  RETIRED_APP_SEGMENTS
} from '@/shared/routes/paths';

// Public legal pages. Lazy (unlike the two prerendered marketing pages): their
// content is API-driven, so there is no static markup a Suspense fallback would
// blank on first paint.
const Legal = lazy(() => import('@/pages/public/pages/Legal'));
const PrivacyChoices = lazy(
  () => import('@/pages/public/pages/PrivacyChoices')
);

const LoginPage = lazy(() => import('@/pages/Login'));

const ROICalculator = lazy(() => import('@/pages/ROICalculator'));
const Profile = lazy(() => import('@/pages/Profile'));
const Organization = lazy(() => import('@/pages/Organization'));
const Reports = lazy(() => import('@/pages/Reports'));
const Analytics = lazy(() => import('@/pages/Analytics'));
const BrandPerformance = lazy(() => import('@/pages/BrandPerformance'));
const GiftCards = lazy(() => import('@/pages/GiftCards'));
const Programs = lazy(() => import('@/pages/Programs'));
const Settings = lazy(() => import('@/pages/Settings'));
const KnowledgeBase = lazy(() => import('@/pages/KnowledgeBase'));
const BagOrder = lazy(() => import('@/pages/BagOrder'));
const Dropshipping = lazy(() => import('@/pages/Dropshipping'));
const Cashout = lazy(() => import('@/pages/Cashout'));
const CleanoutLandingBuilder = lazy(
  () => import('@/pages/CleanoutLandingBuilder')
);
const Newsroom = NEWSROOM_V3_ENABLED
  ? lazy(() => import('@/pages/Newsroom'))
  : null;
const NotFound = lazy(() => import('@/pages/NotFound'));

// One element per editor, shared by its canonical route and its program step
// routes, so a step URL can never mount an editor behind a weaker gate.
const EDITOR_ELEMENTS: Record<ProgramEditorKey, React.ReactElement> = {
  'cleanout-landing': (
    <FeatureGuard featureKey={PROGRAM_EDITORS['cleanout-landing'].feature}>
      <CleanoutLandingBuilder />
    </FeatureGuard>
  ),
  'cashout-tile': (
    <FeatureGuard featureKey={PROGRAM_EDITORS['cashout-tile'].feature}>
      <Cashout />
    </FeatureGuard>
  ),
  // Only reachable while newsroom.v3 is on; the registry drops its step
  // route otherwise.
  newsroom: Newsroom ? (
    <FeatureGuard featureKey={PROGRAM_EDITORS.newsroom.feature}>
      <Newsroom />
    </FeatureGuard>
  ) : (
    <Navigate to={APP_ROUTES.PROGRAMS} replace={true} />
  )
};

// A step URL needs every key (module, v3 rollout, program, editor); any miss
// lands on Programs. Modal steps live on the program's home step, so their URL
// forwards there with the param that opens the modal.
const programStepElement = ({
  program,
  step
}: ProgramStepRoute): React.ReactElement => (
  <FeatureGuard
    featureKey={stepRequiredFeatures(program, step)}
    match="all"
    redirectTo={APP_ROUTES.PROGRAMS}
  >
    {step.surface === 'editor' ? (
      EDITOR_ELEMENTS[step.editor]
    ) : (
      <Navigate
        to={`${programHomePath(program)}?${ROADMAP_STEP_PARAM}=${step.segment}`}
        replace={true}
      />
    )}
  </FeatureGuard>
);

// Design-system gallery. Deliberately outside the auth gate: a gallery that
// needs a WorkOS session and a live API is a gallery nobody opens.
// See src/pages/_KitchenSink/index.tsx.
//
// The import() must sit INSIDE the import.meta.env.DEV branch. Vite replaces
// that expression with `false` at transform time and Rollup then drops the
// whole branch; a top-level `lazy(() => import(...))` guarded only at the route
// still emits a production chunk (verified: it shipped a 12.7 kB asset).
const KitchenSink = import.meta.env.DEV
  ? lazy(() => import('@/pages/_KitchenSink'))
  : null;

// Exported without the surrounding <Router> so tests can drive the tree with a
// MemoryRouter. Everything about the layout nesting and route ranking below is
// invisible to tsc, so src/routes/__tests__/routes.test.tsx is the only check.
export const AppRouteTree: React.FC = () => (
  <Routes>
    <Route path={PUBLIC_ROUTES.LANDING} element={<RaaSLanding />} />
    <Route path={PUBLIC_ROUTES.CONTACT} element={<ContactUs />} />
    <Route path={LEGAL_ROUTES.PRIVACY_CHOICES} element={<PrivacyChoices />} />
    <Route path="/legal/:slug" element={<Legal />} />
    <Route
      path={PUBLIC_ROUTES.REQUEST_ACCESS}
      element={<Navigate to={PUBLIC_ROUTES.LANDING} replace={true} />}
    />
    <Route path={LOGIN_ROUTES.SPLAT} element={<LoginPage />} />
    <Route
      path={LOGIN_ROUTES.LEGACY_AUTH_SPLAT}
      element={<LegacyAuthRedirect />}
    />

    <Route path={APP_ROUTES.ROOT} element={<ProtectedRoute />}>
      <Route index={true} element={<DefaultRedirect />} />

      <Route element={<AppShell />}>
        <Route path={APP_SEGMENTS.PROFILE} element={<Profile />} />
        <Route path={APP_SEGMENTS.ORGANIZATION} element={<Organization />} />
        <Route path={APP_SEGMENTS.SETTINGS} element={<Settings />} />
        <Route
          path={APP_SEGMENTS.ROI_CALCULATOR}
          element={
            <FeatureGuard featureKey="ROI_CALCULATOR">
              <ROICalculator />
            </FeatureGuard>
          }
        />
        <Route
          path={APP_SEGMENTS.REPORTS}
          element={
            <FeatureGuard featureKey={['REPORTS', 'BULK_CONSIGNMENT_REPORTS']}>
              <BrandGuard>
                <Reports />
              </BrandGuard>
            </FeatureGuard>
          }
        />
        <Route
          path={APP_SEGMENTS.ANALYTICS}
          element={
            <FeatureGuard featureKey="ANALYTICS">
              <BrandGuard>
                <Analytics />
              </BrandGuard>
            </FeatureGuard>
          }
        />
        <Route
          path={APP_SEGMENTS.BRAND_PERFORMANCE_THREDUP}
          element={
            <FeatureGuard featureKey="BRAND_PERFORMANCE_THREDUP">
              <BrandPerformance />
            </FeatureGuard>
          }
        />
        <Route
          path={APP_SEGMENTS.GIFT_CARDS}
          element={
            <FeatureGuard featureKey="GIFT_CARDS">
              <GiftCards />
            </FeatureGuard>
          }
        />
        <Route
          path={APP_SEGMENTS.PROGRAMS}
          element={
            <FeatureGuard featureKey="PROGRAMS">
              <Programs />
            </FeatureGuard>
          }
        />
        {/* Matching is case-insensitive, so /CLEAN_OUT/ links still resolve. */}
        {PROGRAM_STEP_ROUTES.map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={programStepElement(route)}
          />
        ))}
        <Route
          path={PROGRAM_STEPS_FALLBACK_PATH}
          element={<Navigate to={APP_ROUTES.PROGRAMS} replace={true} />}
        />
        <Route
          path={APP_SEGMENTS.KNOWLEDGE_BASE}
          element={
            <FeatureGuard featureKey="KNOWLEDGE_BASE">
              <KnowledgeBase />
            </FeatureGuard>
          }
        />
        <Route
          path={APP_SEGMENTS.KITS_ORDERING}
          element={
            <FeatureGuard featureKey="KITS_ORDERING">
              <BagOrder />
            </FeatureGuard>
          }
        />
        <Route
          path={APP_SEGMENTS.CASHOUT_TILE}
          element={EDITOR_ELEMENTS['cashout-tile']}
        />
        <Route
          path={APP_SEGMENTS.CLEANOUT_LANDING_BUILDER}
          element={EDITOR_ELEMENTS['cleanout-landing']}
        />
        {Newsroom ? (
          <Route
            path={APP_SEGMENTS.NEWSROOM}
            element={EDITOR_ELEMENTS.newsroom}
          />
        ) : null}
        <Route
          path={`${APP_SEGMENTS.DROPSHIPPING}/*`}
          element={
            <FeatureGuard featureKey="DROPSHIPPING">
              <Dropshipping />
            </FeatureGuard>
          }
        />
        <Route path={APP_SEGMENTS.NOT_FOUND} element={<NotFound />} />
        <Route path="*" element={<NotFound />} />
      </Route>

      {RETIRED_APP_SEGMENTS.map((segment) => (
        <Route
          key={segment}
          path={`${segment}/*`}
          element={<Navigate to={APP_ROUTES.PROGRAMS} replace={true} />}
        />
      ))}
    </Route>

    {KitchenSink ? (
      <Route path="/_kitchen-sink" element={<KitchenSink />} />
    ) : null}

    {LEGACY_APP_PATHS.map((path) => (
      <Route key={path} path={path} element={<LegacyAppRedirect />} />
    ))}

    <Route path="*" element={<NotFound />} />
  </Routes>
);

const AppRoutes: React.FC = () => (
  <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
    <ScrollToTop />
    {/*
        Inside <Router> because it reads useLocation. PostHog needs no equivalent
        — it detects navigation via its own history patch — but HubSpot's SPA
        detection is unreliable, so its pageviews are driven from the router.
      */}
    <HubSpotPageViews />
    <AppRouteTree />
  </Router>
);

export default AppRoutes;
